import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Package, Users, DollarSign, ShoppingBag, Activity, Truck, GraduationCap, CheckCircle2, XCircle, AlertCircle, Search, Link as LinkIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SkuHealthPanel } from "../components/SkuHealthPanel";
import { MarginBadge, marginOf } from "../components/MarginBadge";
import { ProfitPanel } from "../components/ProfitPanel";

const emptyForm = { name: "", slug: "", description: "", price: 0, wholesale_price: 0, category: "notebooks", image: "", stock: 100, bulk_min_qty: 12, tags: [], featured: false, supplier_sku: "" };

export const Admin = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [teacherReqs, setTeacherReqs] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [repricing, setRepricing] = useState(false);

  const repriceLowMargins = async () => {
    if (!window.confirm("Reprice every amber/red product to 2.5× retail and 1.6× wholesale of CJ cost?")) return;
    setRepricing(true);
    try {
      const { data } = await api.post("/admin/products/reprice-low-margins");
      toast.success(`Repriced ${data.repriced} product(s)`);
      load();
    } catch (err) {
      toast.error("Bulk reprice failed");
    } finally {
      setRepricing(false);
    }
  };

  const load = async () => {
    try {
      const [s, p, o, e, t, sup] = await Promise.all([
        api.get("/admin/stats"), api.get("/products"),
        api.get("/admin/orders"), api.get("/admin/wholesaler-events"),
        api.get("/admin/teacher-requests"), api.get("/admin/supplier-status"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data); setEvents(e.data);
      setTeacherReqs(t.data); setSupplier(sup.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    }
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const createProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: parseFloat(form.price), wholesale_price: parseFloat(form.wholesale_price), stock: parseInt(form.stock), bulk_min_qty: parseInt(form.bulk_min_qty) });
      toast.success("Product added");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`); load();
  };

  const approveTeacher = async (uid) => { await api.post(`/admin/teacher-requests/${uid}/approve`); toast.success("Teacher approved"); load(); };
  const rejectTeacher = async (uid) => { await api.post(`/admin/teacher-requests/${uid}/reject`); toast.success("Teacher request rejected"); load(); };

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null); // product being linked
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const [manualSku, setManualSku] = useState("");

  const openLink = (p) => {
    setLinkTarget(p); setLinkQuery(p.name); setLinkResults([]); setManualSku(p.supplier_sku || "");
    setLinkOpen(true);
  };
  const runSearch = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.get("/admin/cj/search", { params: { q: linkQuery } });
      setLinkResults(data.results || []);
      if (!data.results?.length) toast.info("No results — try different keywords or paste the VID manually below");
    } catch (err) {
      const msg = err.response?.data?.detail || "";
      if (msg.includes("Too Many Requests") || err.response?.status === 502) {
        toast.error("CJ hit a rate limit (shared IP quota). Paste the VID manually below — it works the same.");
      } else {
        toast.error(msg || "CJ search unavailable — check credentials");
      }
    } finally { setLinkBusy(false); }
  };
  const saveSku = async (sku) => {
    try {
      await api.patch(`/admin/products/${linkTarget.product_id}/supplier-sku`, { supplier_sku: sku });
      toast.success(sku ? "Product linked to CJ" : "Link cleared");
      setLinkOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const syncFromCj = async (p) => {
    if (!p.supplier_sku) { toast.error("Link the product to a CJ VID first"); return; }
    try {
      const { data } = await api.post(`/admin/products/${p.product_id}/cj-sync`);
      toast.success(`Synced from CJ (${data.updated.length ? data.updated.join(", ") : "no changes"})`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
  };

  const fireTestOrder = async () => {
    try {
      const { data } = await api.post("/admin/test-order");
      toast.success("Test order fired — watch the pipeline advance", { duration: 5000 });
      load();
      setTimeout(load, 4000);
      setTimeout(load, 8000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-6">Admin dashboard</h1>

      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={fireTestOrder} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="admin-fire-test-order"><Truck className="w-4 h-4 mr-1" />Fire a test order</Button>
        <span className="text-xs text-slate-500 self-center">Creates a paid demo order for you and runs it through the pipeline.</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Revenue" value={stats ? `$${stats.revenue.toFixed(2)}` : "—"} color="bg-emerald-50 text-emerald-700" />
        <StatCard icon={ShoppingBag} label="Paid orders" value={stats?.paid_orders ?? "—"} color="bg-indigo-50 text-indigo-700" />
        <StatCard icon={Package} label="Products" value={stats?.products_count ?? "—"} color="bg-amber-50 text-amber-700" />
        <StatCard icon={Users} label="Customers" value={stats?.users_count ?? "—"} color="bg-fuchsia-50 text-fuchsia-700" />
      </div>

      {stats?.fulfillment && (
        <div className="mb-8 p-5 bg-white border border-slate-200 rounded-2xl">
          <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" />Wholesaler pipeline</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(stats.fulfillment).map(([k, v]) => (
              <div key={k} className="p-3 rounded-xl bg-slate-50">
                <div className="text-xs text-slate-500 capitalize">{k.replace("_", " ")}</div>
                <div className="font-display font-extrabold text-2xl">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {supplier && (
        <>
        <div className={`mb-4 p-5 rounded-2xl border ${supplier.configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="admin-supplier-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${supplier.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                <Truck className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold">Auto-fulfillment supplier: {supplier.configured ? "CJ Dropshipping (LIVE)" : "Mock (setup required)"}</div>
                <div className="text-sm text-slate-600 mt-1">
                  {supplier.configured
                    ? `Paid orders auto-forward to CJ. ${supplier.products_linked}/${supplier.products_total} products linked.`
                    : "Add CJ Dropshipping credentials to go live. Until then, orders fulfill via the built-in mock pipeline."}
                </div>
                {!supplier.configured && (
                  <ol className="text-sm text-slate-700 mt-3 space-y-1 list-decimal list-inside">
                    <li>Sign up (free) at <a className="text-indigo-600 underline" href="https://app.cjdropshipping.com/register.html" target="_blank" rel="noreferrer">cjdropshipping.com/register</a></li>
                    <li>In CJ dashboard sidebar → <em>Apps</em> → <em>Install App</em> → find <strong>API</strong> under "Others" and install it</li>
                    <li>Click your avatar → <em>Personal Center</em> → <em>API</em> tab → <strong>Add API</strong> → name it "SupplySprout", type = API Key → <em>Confirm</em>, then copy the key</li>
                    <li>Paste into <code className="bg-white px-1 rounded">/app/backend/.env</code> as <code className="bg-white px-1 rounded">CJ_API_KEY</code>, and set <code className="bg-white px-1 rounded">SUPPLIER_MODE=cj</code>, then restart backend</li>
                    <li>For each product below, click "Find on CJ" and link it (or paste the VID manually)</li>
                  </ol>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={`mb-8 p-5 rounded-2xl border ${supplier.faire_configured ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`} data-testid="admin-faire-card">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${supplier.faire_configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
              <GraduationCap className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">B2B wholesale: Faire {supplier.faire_configured ? "(LIVE)" : "(optional)"}</div>
              <div className="text-sm text-slate-600 mt-1">
                {supplier.faire_configured
                  ? "Wholesale quotes from schools auto-forward to Faire."
                  : "Add a Faire Partner API access token to auto-forward wholesale quotes. Until then, quotes stay in the Quotes table below."}
              </div>
              {!supplier.faire_configured && (
                <ol className="text-sm text-slate-700 mt-3 space-y-1 list-decimal list-inside">
                  <li>Sign in as a brand at <a className="text-indigo-600 underline" href="https://www.faire.com/brand-portal" target="_blank" rel="noreferrer">Faire Brand Portal</a></li>
                  <li>Go to <em>Settings → Partners</em> → generate an <strong>Access Token</strong> for SupplySprout</li>
                  <li>Add to <code className="bg-white px-1 rounded">/app/backend/.env</code>: <code className="bg-white px-1 rounded">FAIRE_ACCESS_TOKEN=...</code> and <code className="bg-white px-1 rounded">FAIRE_MODE=faire</code>, then restart</li>
                </ol>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      <Tabs defaultValue="products">
        <TabsList className="flex-wrap h-auto justify-start">
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="admin-tab-teachers">Teacher Requests {teacherReqs.filter(t => t.teacher_verification_status === "pending").length > 0 && <span className="ml-1 bg-indigo-600 text-white rounded-full text-[10px] px-1.5">{teacherReqs.filter(t => t.teacher_verification_status === "pending").length}</span>}</TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-tab-events">Wholesaler Events</TabsTrigger>
          <TabsTrigger value="sku-health" data-testid="admin-tab-sku-health">SKU Health</TabsTrigger>
          <TabsTrigger value="profit" data-testid="admin-tab-profit">Profit</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="admin-product-create-btn"><Plus className="w-4 h-4 mr-1" />New product</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create product</DialogTitle></DialogHeader>
                <form onSubmit={createProduct} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} /></div>
                    <div><Label>Category</Label>
                      <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                        <option value="notebooks">Notebooks</option>
                        <option value="writing">Writing</option>
                        <option value="art">Art</option>
                        <option value="backpacks">Backpacks</option>
                        <option value="classroom">Classroom</option>
                        <option value="tech">Tech</option>
                      </select>
                    </div>
                  </div>
                  <div><Label>Description</Label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div><Label>Image URL</Label><Input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Retail price ($)</Label><Input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
                    <div><Label>Wholesale price ($)</Label><Input required type="number" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} /></div>
                    <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
                    <div><Label>Bulk min qty</Label><Input type="number" value={form.bulk_min_qty} onChange={(e) => setForm({ ...form, bulk_min_qty: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Supplier SKU (CJ VID) — optional</Label><Input value={form.supplier_sku} onChange={(e) => setForm({ ...form, supplier_sku: e.target.value })} placeholder="e.g. 1F8B6C23-…" data-testid="supplier-sku-input" /></div>
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full">Create</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div className="text-sm text-slate-600" data-testid="admin-margin-summary">
            {(() => {
              const thin = products.filter((p) => { const m = marginOf(p); return m != null && m < 15; }).length;
              const low = products.filter((p) => { const m = marginOf(p); return m != null && m >= 15 && m < 30; }).length;
              return thin + low === 0
                ? "All products have healthy margins (30%+ on both retail and wholesale)."
                : `${thin} thin-margin (<15%) and ${low} low-margin (15–29%) product(s). Hover a badge for details.`;
            })()}
          </div>
          {products.some((p) => { const m = marginOf(p); return m != null && m < 30; }) && (
            <Button onClick={repriceLowMargins} disabled={repricing} size="sm" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="admin-fix-all-margins-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${repricing ? "animate-spin" : ""}`} />Fix all low margins
            </Button>
          )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-3">Product</th><th className="p-3">Category</th>
                  <th className="p-3">Price</th><th className="p-3">Wholesale</th><th className="p-3">Margin</th>
                  <th className="p-3">Stock</th><th className="p-3">Supplier SKU</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-t border-slate-100">
                    <td className="p-3 flex items-center gap-2"><img src={p.image} className="w-10 h-10 rounded-lg object-cover" alt="" />{p.name}</td>
                    <td className="p-3 capitalize">{p.category}</td>
                    <td className="p-3">${p.price.toFixed(2)}</td>
                    <td className="p-3">${p.wholesale_price.toFixed(2)}</td>
                    <td className="p-3"><MarginBadge product={p} /></td>
                    <td className="p-3">{p.stock}</td>
                    <td className="p-3 font-mono text-xs">{p.supplier_sku || <span className="text-amber-600 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" />unlinked</span>}</td>
                    <td className="p-3 text-right space-x-1">
                      <button onClick={() => openLink(p)} className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`admin-link-cj-${p.product_id}`}><LinkIcon className="w-3 h-3" />Find on CJ</button>
                      {p.supplier_sku && (
                        <button onClick={() => syncFromCj(p)} className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold ml-2" data-testid={`admin-sync-cj-${p.product_id}`}><RefreshCw className="w-3 h-3" />Sync</button>
                      )}
                      <button onClick={() => del(p.product_id)} className="text-slate-400 hover:text-red-500 ml-2" data-testid={`admin-del-${p.product_id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="teachers" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {teacherReqs.length === 0 ? <div className="text-sm text-slate-500 p-6 text-center">No teacher verification requests yet.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr><th className="p-3">Teacher</th><th className="p-3">School</th><th className="p-3">Grade / Subject</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {teacherReqs.map((t) => (
                    <tr key={t.user_id} className="border-t border-slate-100" data-testid={`teacher-req-${t.user_id}`}>
                      <td className="p-3">
                        <div className="font-semibold flex items-center gap-1">{t.name}{t.is_verified_teacher && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}</div>
                        <div className="text-xs text-slate-500">{t.email}</div>
                      </td>
                      <td className="p-3">
                        <div>{t.teacher_verification?.school_name}</div>
                        <div className="text-xs text-slate-500">{t.teacher_verification?.school_email}</div>
                      </td>
                      <td className="p-3 text-xs">{t.teacher_verification?.grade_level} · {t.teacher_verification?.subject_taught}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          t.teacher_verification_status === "approved" ? "bg-emerald-50 text-emerald-700" :
                          t.teacher_verification_status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                          {t.teacher_verification_status}
                        </span>
                      </td>
                      <td className="p-3 text-right space-x-2">
                        {t.teacher_verification_status !== "approved" && (
                          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => approveTeacher(t.user_id)} data-testid={`teacher-approve-${t.user_id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                          </Button>
                        )}
                        {t.teacher_verification_status !== "rejected" && (
                          <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-7 text-xs" onClick={() => rejectTeacher(t.user_id)} data-testid={`teacher-reject-${t.user_id}`}>
                            <XCircle className="w-3 h-3 mr-1" />Reject
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr><th className="p-3">Order</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Fulfillment</th><th className="p-3">Created</th></tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_id} className="border-t border-slate-100">
                    <td className="p-3 font-mono text-xs">{o.order_id}</td>
                    <td className="p-3">${o.total.toFixed(2)}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{o.payment_status}</span></td>
                    <td className="p-3 capitalize">{o.fulfillment_status.replace("_", " ")}</td>
                    <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="events" className="mt-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            {events.length === 0 ? <div className="text-sm text-slate-500 p-4">No events yet.</div> : events.map((e) => (
              <div key={e.event_id} className="py-2 border-b last:border-0 border-slate-100 text-sm flex justify-between items-center">
                <div><span className="font-mono text-xs text-slate-500">{e.order_id}</span> · <span className="capitalize font-semibold">{e.stage}</span> · {e.message}</div>
                <div className="text-xs text-slate-500">{new Date(e.at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sku-health" className="mt-6">
          <SkuHealthPanel />
        </TabsContent>

        <TabsContent value="profit" className="mt-6">
          <ProfitPanel />
        </TabsContent>
      </Tabs>

      {/* CJ Linking Dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link to CJ Dropshipping</DialogTitle>
          </DialogHeader>
          {linkTarget && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <img src={linkTarget.image} className="w-12 h-12 rounded-lg object-cover" alt="" />
                <div>
                  <div className="font-semibold">{linkTarget.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{linkTarget.product_id}</div>
                </div>
              </div>

              <div>
                <Label>Search CJ catalog</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="e.g. composition notebook" data-testid="cj-search-input" />
                  <Button onClick={runSearch} disabled={linkBusy || !linkQuery.trim()} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="cj-search-btn">
                    <Search className="w-4 h-4 mr-1" />{linkBusy ? "Searching…" : "Search"}
                  </Button>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Tip: In preview environments CJ may rate-limit search (shared IP). If it does, browse <a href={`https://cjdropshipping.com/list/wholesale-${encodeURIComponent(linkQuery)}.html`} target="_blank" rel="noreferrer" className="text-indigo-600 underline">CJ directly</a> and paste the VID below — it's on every product page.
                </div>
              </div>

              {linkResults.length > 0 && (
                <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-xl divide-y">
                  {linkResults.map((r) => (
                    <div key={r.vid} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`cj-result-${r.vid}`}>
                      {r.image && <img src={r.image} className="w-12 h-12 rounded-lg object-cover" alt="" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm line-clamp-1">{r.name}</div>
                        <div className="text-xs text-slate-500">VID: <span className="font-mono">{r.vid}</span> · ${r.price}</div>
                      </div>
                      <Button size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={() => saveSku(r.vid)}>Link</Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2 border-t border-slate-100">
                <Label>Or paste a CJ VID directly</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={manualSku} onChange={(e) => setManualSku(e.target.value)} placeholder="1F8B6C23-…" data-testid="cj-manual-sku-input" />
                  <Button variant="outline" onClick={() => saveSku(manualSku)} className="rounded-full" data-testid="cj-save-manual-btn">Save</Button>
                </div>
                {linkTarget.supplier_sku && (
                  <button onClick={() => saveSku("")} className="text-xs text-red-500 mt-2 hover:underline">Clear link</button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="p-5 bg-white rounded-2xl border border-slate-200">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
    <div className="text-xs text-slate-500 mt-3">{label}</div>
    <div className="font-display font-extrabold text-2xl">{value}</div>
  </div>
);
